<template>
  <div>
    <Header :selectedApp="selectedApp"></Header>
    <div class="col-12 d-inline-flex">
      <div class="col-10 no-overflow ms-3 me-3 pt-3" style="position: relative;">
        <h2>Payment Management <small v-if="selectedApp" class="text-muted fs-6">&mdash; {{ selectedApp }}</small></h2>

        <div class="d-flex justify-content-between align-items-center mb-3">
          <div>
            CC share config:
            <span class="badge fs-6" :class="isValidTotal ? 'bg-success' : 'bg-danger'">{{ ccTotal }}%</span>
            <span v-if="!isValidTotal" class="text-muted ms-2">(must total 100% to save)</span>
          </div>
        </div>

        <div v-if="error" class="error-message mb-3">{{ error }}</div>

        <div v-if="loading">Loading...</div>

        <template v-else>
          <div class="d-flex flex-wrap gap-3 mb-3">
            <PaymentMethodCard
                v-for="provider in providers"
                :key="provider.key"
                :provider="provider"
                :cc-total="ccTotal"
                @update-instance="payload => onUpdateInstance(provider.key, payload)"
            />
          </div>

          <div class="mt-3">
            <a role="button" class="procricket-btn me-2" @click="save">Save</a>
          </div>
        </template>

        <!-- PAGE HELP / EXPLANATION -->
        <div class="mt-3">
          <div class="channel-details-card p-3">
            <div class="d-flex align-items-center justify-content-between">
              <h5 class="mb-0">How this page works</h5>
              <button
                  class="btn btn-sm btn-outline-secondary"
                  type="button"
                  @click="showHelp = !showHelp"
              >
                {{ showHelp ? 'Hide' : 'Show' }}
              </button>
            </div>

            <div v-show="showHelp" class="mt-3 small">
              <div class="mb-3">
                <div class="fw-semibold mb-1">What this page controls</div>
                <ul class="mb-0">
                  <li>Every payment method has a <strong>Percentage</strong>. Any method whose percentage isn't null counts as a CC method (<strong>Stripe, Square, PayPal and cPay</strong>) and shares one Credit Card pool — their Percentage is a weighted draw and must total exactly <strong>100%</strong> across all active accounts.</li>
                  <li>For <strong>PayPal</strong> specifically: as long as one PayPal instance is active, PayPal payments can still be completed. The Percentage here only configures the PayPal SDK variant, so disabling every PayPal instance to turn off the SDK variant isn't the right approach.</li>
                  <li><strong>Plisio</strong> sits outside that pool (it's crypto, not card-based) — it doesn't have a Percentage since it doesn't need one, but its Active/Status are still managed here for consistency.</li>
                  <li>An account's <strong>On/Off</strong> toggle sets both its <strong>Active</strong> and <strong>Status</strong> values together.</li>
                </ul>
              </div>

              <div class="mb-3">
                <div class="fw-semibold mb-1">Editing</div>
                <ul class="mb-0">
                  <li>Type directly into an account's percentage field, and use the toggle to activate or deactivate it.</li>
                  <li>The badge next to <strong>CC share config</strong> at the top shows the running total — it must read exactly 100% before <strong>Save</strong> is enabled.</li>
                  <li><strong>Save</strong> commits every change on the page at once.</li>
                </ul>
              </div>

              <div class="mb-3">
                <div class="fw-semibold mb-1">Only editable here</div>
                <ul class="mb-0">
                  <li><strong>Percentage, Active and Status</strong> for Stripe, Square, PayPal and cPay are locked everywhere else — their individual configuration pages disable these fields and link back to this page. <strong>Plisio's</strong> Active/Status can still be edited on its own configuration page too, since it sits outside the Credit Card pool.</li>
                </ul>
              </div>

              <div>
                <div class="fw-semibold mb-1">Not editable here</div>
                <ul class="mb-0">
                  <li>cPay's <strong>WhatsApp share</strong> badge shown here is a read-only preview — change it on the cPay Configuration page.</li>
                  <li>Connection details for every provider (API keys, secrets, webhook URLs, checkout pages, etc.) are edited on that provider's own configuration page, not here.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <SuccessModal
        :show="showSuccessModal"
        title="Saved"
        message="Payment configuration saved."
        @close="showSuccessModal = false"
    />
  </div>
</template>

<script>
import AdminService from "@/services/admin-service";
import Header from "@/components/Header.vue";
import PaymentMethodCard from "@/components/PaymentMethodCard.vue";
import SuccessModal from "@/components/SuccessModal.vue";
import Cookies from "js-cookie";
import { CardPaymentConfiguration, CardPaymentConfigurations } from "@/model/models";

// Display order + labels for the five provider groups. Percentage-bearing rows (Stripe,
// Square, PayPal, cPay) come from the aggregate card-payment-configuration endpoint and
// share one 100% pool. Plisio has no percentage field at all — it's kept outside that pool
// (crypto stays random-among-active / renew-only) and is fetched from its own endpoint.
const PROVIDER_LABELS = {
  STRIPE: "Stripe",
  SQUARE: "Square",
  PAYPAL: "PayPal",
  CPAY: "cPay",
  PLISIO: "Plisio"
};
const PROVIDER_ORDER = ["STRIPE", "SQUARE", "PAYPAL", "CPAY", "PLISIO"];

export default {
  name: "PaymentManagement",
  components: { Header, PaymentMethodCard, SuccessModal },
  data() {
    return {
      providers: [],
      // Raw Plisio configs kept around so Save can send the full object back (its update
      // endpoint has no lightweight toggle-only variant like the card endpoint does).
      plisioConfigs: [],
      selectedApp: "",
      error: "",
      loading: true,
      showHelp: true,
      showSuccessModal: false
    };
  },
  computed: {
    ccTotal() {
      return this.providers
          .flatMap(p => p.instances)
          .filter(i => i.active && i.percentage !== null && i.percentage !== undefined)
          .reduce((sum, i) => sum + (i.percentage || 0), 0);
    },
    isValidTotal() {
      return this.ccTotal === 100;
    }
  },
  created() {
    const appCookie = Cookies.get('application');
    if (appCookie) {
      this.selectedApp = appCookie;
    }
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      this.error = "";
      Promise.all([
        AdminService.getCardPaymentConfigurationList(),
        AdminService.getPlisioConfigurationList(),
        AdminService.getCpayConfigurationList()
      ]).then(([cardList, plisioList, cpayList]) => {
        this.plisioConfigs = plisioList || [];
        this.providers = this.buildProviders(cardList || [], this.plisioConfigs, cpayList || []);
      }).catch((err) => {
        console.error("Error loading payment configuration:", err);
        this.error = "Failed to load payment configuration.";
      }).finally(() => {
        this.loading = false;
      });
    },
    // Same convention as the other config pages (e.g. StripeConfig.vue): when a specific app
    // is selected in the header, only show rows for that app; '' ("All apps") shows everything.
    // Guarded with `!row.application` so this stays a no-op — nothing gets hidden — until the
    // card endpoint actually returns the field (see payment-management-admin-api-contract.md).
    matchesSelectedApp(row) {
      if (!this.selectedApp) return true;
      return !row.application || row.application === this.selectedApp;
    },
    buildProviders(cardList, plisioList, cpayList) {
      const grouped = {};
      PROVIDER_ORDER.forEach((key) => {
        grouped[key] = {
          key,
          label: PROVIDER_LABELS[key],
          instances: []
        };
      });

      cardList.filter(this.matchesSelectedApp).forEach((row) => {
        if (!grouped[row.provider]) return;
        // WhatsApp share is a cPay-only, backend-managed flag — read here purely for
        // preview, sourced from cPay's own list since the aggregate card endpoint
        // doesn't carry it. Left undefined for every other provider.
        const whatsappEnabled = row.provider === 'CPAY'
            ? cpayList.find(c => c.cpayConfigurationId === row.configurationId)?.whatsappEnabled
            : undefined;
        grouped[row.provider].instances.push({
          provider: row.provider,
          configurationId: row.configurationId,
          name: row.name,
          percentage: row.percentage,
          active: row.active,
          status: row.status !== undefined ? row.status : row.active,
          application: row.application,
          whatsappEnabled
        });
      });

      plisioList.filter(this.matchesSelectedApp).forEach((row) => {
        grouped.PLISIO.instances.push({
          provider: 'PLISIO',
          configurationId: row.plisioConfigurationId,
          name: row.plisioName,
          percentage: null,
          active: row.active,
          status: row.status,
          application: row.application
        });
      });

      return PROVIDER_ORDER
          .map((key) => grouped[key])
          .filter((provider) => provider.instances.length > 0);
    },
    onUpdateInstance(providerKey, { configurationId, field, value }) {
      const provider = this.providers.find(p => p.key === providerKey);
      if (!provider) return;
      const instance = provider.instances.find(i => i.configurationId === configurationId);
      if (!instance) return;
      instance[field] = value;
      if (field === 'active') {
        instance.status = value;
      }
    },
    save() {
      if (!this.isValidTotal) {
        this.error = `Credit Card percentages total ${this.ccTotal}%, they must sum to 100% before saving.`;
        return;
      }
      this.error = "";

      const cardRows = this.providers
          .filter(p => p.key !== 'PLISIO')
          .flatMap(p => p.instances)
          .map(i => new CardPaymentConfiguration(
              i.provider, i.configurationId, i.name, i.percentage, i.active, i.status, i.application
          ));

      const plisioProvider = this.providers.find(p => p.key === 'PLISIO');
      const plisioUpdates = (plisioProvider ? plisioProvider.instances : []).map((instance) => {
        const raw = this.plisioConfigs.find(c => c.plisioConfigurationId === instance.configurationId);
        if (!raw) return Promise.resolve();
        const updated = { ...raw, active: instance.active, status: instance.status };
        return AdminService.updatePlisioConfiguration(updated);
      });

      Promise.all([
        AdminService.saveCardPaymentConfigurations(new CardPaymentConfigurations(cardRows)),
        ...plisioUpdates
      ]).then(() => {
        this.showSuccessModal = true;
        this.load();
      }).catch((err) => {
        console.error("Error saving payment configuration:", err);
        if (err.response && err.response.data && err.response.data.message) {
          this.error = err.response.data.message;
        } else {
          this.error = "Failed to save payment configuration.";
        }
      });
    }
  }
};
</script>
<style src="../../assets/style.css"></style>
